/**
 * §27.2 card 3, "Contest rules". Mounted as `children` of the
 * `contest-rules` slot (`plan.md ## 5.19`). This component deliberately
 * renders no card shell of its own: `PoolSettingsCardSlot` owns the shell,
 * the title, the tone and the danger spacing.
 *
 * ISSUE-1041: the card now edits. Under the rules it offers Change contest
 * rules for each contest that has not ended (or says why it cannot), the
 * House rules field that writes the Commissioner's preamble, and the saved
 * message naming the round a rule change starts in. The pane only mounts this
 * card for the Commissioner and Admins, so it takes no role prop.
 *
 * This mount always reads live and never serves cached rules: SUR-15 and
 * SUR-52 let a Commissioner change the rules at any time, so this card
 * cannot show a copy from a previous visit.
 */
import {
  useContestList,
  useContestRulesGet,
} from "@sportsmet/contest-sdk/private";
import { usePoolRulesGet } from "@sportsmet/pool-sdk/private";
import { Button, InlineBanner } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import { useOverlay } from "../../routeState/useOverlay";
import { PoolRulesBody } from "../poolRules/PoolRulesBody";
import { resolvePoolRulesState } from "../poolRules/poolRulesState";
import {
  CONTEST_RULES_ENDED_NOTE,
  CONTEST_RULES_LIST_FAILED_MESSAGE,
  CONTEST_RULES_NO_CONTEST_NOTE,
  CONTEST_RULES_RETRY_LABEL,
  EDIT_CONTEST_RULES_DIALOG_NAME,
  resolveContestRulesEditState,
} from "./contestRulesCardState";
import { EditContestRulesDialog } from "./EditContestRulesDialog";
import { PoolSettingsHouseRulesField } from "./PoolSettingsHouseRulesField";

export type typePoolSettingsRulesCardProps = { poolId: string };

export const PoolSettingsRulesCard = ({
  poolId,
}: typePoolSettingsRulesCardProps): ReactNode => {
  const overlay = useOverlay();
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const rules = usePoolRulesGet(
    { poolId },
    { staleTime: 0, refetchOnMount: "always" },
  );
  const contests = useContestList({ poolId });
  const viewerContestId = contests.data?.data?.viewerContestId ?? "";
  const contestRules = useContestRulesGet(
    { poolId, contestId: viewerContestId },
    {
      enabled: viewerContestId !== "",
      staleTime: 0,
      refetchOnMount: "always",
    },
  );
  const contestRulesData = contestRules.data?.data ?? null;

  const state = resolvePoolRulesState({
    isLoading: rules.isLoading,
    isTransportError: rules.isError,
    errorCode: extractErrorCode(rules.data),
    commissionerPreamble: rules.data?.data?.commissionerPreamble ?? null,
    autoGeneratedSummary: rules.data?.data?.autoGeneratedSummary ?? null,
    // The contest shown is the one /contest/list names in viewerContestId
    // (ISSUE-1041 owner default 3).
    contest: contestRulesData
      ? {
          autoGeneratedText: contestRulesData.autoGeneratedText,
          commissionerPreamble: contestRulesData.commissionerPreamble ?? null,
        }
      : null,
  });

  const editState = resolveContestRulesEditState({
    isLoading: contests.isLoading,
    isTransportError: contests.isError,
    errorCode: extractErrorCode(contests.data),
    items: contests.data?.data?.items ?? null,
  });

  let editArea: ReactNode = null;
  if (editState.phase === "ready") {
    editArea = (
      <div className="flex flex-wrap gap-2">
        {editState.targets.map((target) => (
          <Button
            key={target.contestId}
            variant="secondary"
            size="sm"
            data-testid={`pool-settings-contest-rules-edit-${target.contestId}`}
            onClick={() => {
              setSavedMessage(null);
              overlay.open({
                kind: "dialog",
                name: EDIT_CONTEST_RULES_DIALOG_NAME,
                params: { contestId: target.contestId },
              });
            }}
          >
            {target.label}
          </Button>
        ))}
      </div>
    );
  } else if (editState.phase === "noContest" || editState.phase === "ended") {
    editArea = (
      <p
        className="text-caption text-ink-secondary"
        data-testid="pool-settings-contest-rules-note"
      >
        {editState.phase === "noContest"
          ? CONTEST_RULES_NO_CONTEST_NOTE
          : CONTEST_RULES_ENDED_NOTE}
      </p>
    );
  } else if (editState.phase === "failed") {
    editArea = (
      <div data-testid="pool-settings-contest-rules-failed">
        <InlineBanner
          variant="warning"
          message={CONTEST_RULES_LIST_FAILED_MESSAGE}
          action={{
            label: CONTEST_RULES_RETRY_LABEL,
            onAction: () => {
              void contests.refetch();
            },
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="pool-settings-rules-card">
      <PoolRulesBody
        state={state}
        onRetry={() => {
          void rules.refetch();
        }}
      />
      {savedMessage !== null ? (
        <div data-testid="pool-settings-contest-rules-saved">
          <InlineBanner
            variant="success"
            message={savedMessage}
            onDismiss={() => {
              setSavedMessage(null);
            }}
          />
        </div>
      ) : null}
      {editArea}
      {state.phase === "ready" ? (
        <PoolSettingsHouseRulesField
          poolId={poolId}
          savedPreamble={rules.data?.data?.commissionerPreamble ?? null}
          onSaved={() => {
            void rules.refetch();
          }}
        />
      ) : null}
      <EditContestRulesDialog poolId={poolId} onSaved={setSavedMessage} />
    </div>
  );
};
